import { BarChart3, BookOpen, ChevronDown, CircleHelp, History, Info, Route, Telescope } from "lucide-react";
import type { SituationItem } from "../data/interpretation";
import type { SituationAnalysis, SituationDirection } from "../data/situationAnalysis";
import { situationAgreementLabel } from "../data/situationAnalysis";

function directionClass(direction: SituationDirection) {
  if (direction === "tightening") return "tightening";
  if (direction === "easing") return "easing";
  if (direction === "mixed") return "mixed";
  if (direction === "na") return "na";
  return "neutral";
}

function shortEvidence(text: string) {
  const [head] = text.split(" (");
  return head;
}

function validationStatusLabel(status: "ready" | "limited" | "insufficient") {
  if (status === "ready") return "검증 가능";
  if (status === "limited") return "표본 적음";
  return "표본 부족";
}

export default function SituationPanel({ items, analysis }: { items: SituationItem[]; analysis?: SituationAnalysis }) {
  return (
    <section className="situation-panel" aria-labelledby="situation-title">
      <header className="situation-panel__header">
        <div>
          <p className="kicker">상황 요약</p>
          <h2 id="situation-title"><BookOpen size={18}/> 현재 상황, 쉽게 읽기</h2>
        </div>
        <p><Info size={14}/> 수치 예측이 아니라 실제 관측의 방향·전이·충돌을 정리합니다. 신호 일치도는 확률이 아닙니다.</p>
      </header>

      {analysis && (
        <>
          <article className={`analysis-summary analysis-summary--${directionClass(analysis.current.direction)}`}>
            <div className="analysis-summary__label">
              <span>현재 판단</span>
              <strong>{analysis.current.label}</strong>
            </div>
            <div className="analysis-summary__body">
              <h3>{analysis.current.headline}</h3>
              <p>{analysis.current.explanation}</p>
              <div className="analysis-transmission"><Route size={15}/><span>{analysis.current.transmission}</span></div>
            </div>
          </article>

          <article className={`analysis-change analysis-change--${directionClass(analysis.recentChange.direction)}`}>
            <div className="analysis-change__heading">
              <div><History size={15}/><span>지난번보다 뭐가 달라졌나?</span></div>
              <strong>{analysis.recentChange.label}</strong>
            </div>
            <h3>{analysis.recentChange.headline}</h3>
            <p>{analysis.recentChange.explanation}</p>
            {(analysis.recentChange.highlights.length > 0 || analysis.recentChange.counterSignals.length > 0) && (
              <div className="analysis-change__details">
                {analysis.recentChange.highlights.length > 0 && <div><b>주요 변화</b><ul>{analysis.recentChange.highlights.slice(0, 3).map((text) => <li key={text}>{shortEvidence(text)}</li>)}</ul></div>}
                {analysis.recentChange.counterSignals.length > 0 && <div><b>반대 변화</b><ul>{analysis.recentChange.counterSignals.slice(0, 2).map((text) => <li key={text}>{shortEvidence(text)}</li>)}</ul></div>}
              </div>
            )}
          </article>

          <div className="analysis-evidence-grid">
            <article>
              <span className="analysis-mini-label">주된 근거</span>
              {analysis.current.drivers.length > 0
                ? <ul>{analysis.current.drivers.slice(0, 3).map((text) => <li key={text}>{shortEvidence(text)}</li>)}</ul>
                : <p>한 방향으로 충분히 모인 근거가 없습니다.</p>}
            </article>
            <article>
              <span className="analysis-mini-label">반대·충돌 신호</span>
              {analysis.current.counterSignals.length > 0
                ? <ul>{analysis.current.counterSignals.slice(0, 3).map((text) => <li key={text}>{shortEvidence(text)}</li>)}</ul>
                : <p>현재 확인된 주요 반대 신호가 많지 않습니다.</p>}
            </article>
          </div>

          <div className="analysis-outlook-heading">
            <div><Telescope size={16}/><strong>앞으로의 방향 압력</strong></div>
            <p>“몇 달 뒤 얼마”가 아니라 최근 흐름이 이어질 경우 어느 쪽 압력이 우세한지만 봅니다. 아래 lead/lag의 전이시차 분석과는 별개입니다.</p>
          </div>
          <div className="analysis-outlook-grid">
            {analysis.outlooks.map((outlook) => (
              <article key={outlook.horizon} className={`analysis-outlook analysis-outlook--${directionClass(outlook.direction)}`}>
                <div className="analysis-outlook__top">
                  <span>{outlook.horizon}</span>
                  <strong>{outlook.headline}</strong>
                </div>
                <div className="analysis-outlook__agreement">
                  신호 일치도 {situationAgreementLabel(outlook.agreement)}
                  {outlook.consideredSignals > 0 && <small>{outlook.alignedSignals}/{outlook.consideredSignals}</small>}
                </div>
                <p>{outlook.explanation}</p>
                {outlook.drivers.length > 0 && (
                  <details>
                    <summary>왜 이렇게 보나요?</summary>
                    <ul>{outlook.drivers.map((driver) => <li key={driver}>{driver}</li>)}</ul>
                    {outlook.counterSignals.length > 0 && <><b>반대 신호</b><ul>{outlook.counterSignals.map((signal) => <li key={signal}>{signal}</li>)}</ul></>}
                  </details>
                )}
                <details>
                  <summary>판단이 바뀌는 조건</summary>
                  <ul>{outlook.reversalConditions.map((condition) => <li key={condition}>{condition}</li>)}</ul>
                </details>
              </article>
            ))}
          </div>

          <details className="analysis-validation-details">
            <summary><BarChart3 size={16}/><span><strong>과거 방향검증</strong><small>고급 검증 보기</small></span></summary>
            <div className="analysis-validation-heading">
              <p>당시 신호가 이후 3·6·12개월의 실물 방향과 같은 쪽이었는지 사후 확인합니다. 50% 안팎이면 뚜렷한 방향 우위가 확인되지 않은 것으로 읽으며, 예측확률은 아닙니다.</p>
            </div>
            <div className="analysis-validation-grid">
              {[...new Set(analysis.historicalValidation.map((row) => row.targetId))].map((targetId) => {
                const rows = analysis.historicalValidation.filter((row) => row.targetId === targetId);
                const label = rows[0]?.targetLabel ?? targetId;
                return (
                  <article className="analysis-validation-card" key={targetId}>
                    <div className="analysis-validation-card__top"><strong>{label}</strong><small>미래 실제방향과 비교</small></div>
                    <div className="analysis-validation-rows">
                      {rows.map((row) => (
                        <div key={`${row.targetId}-${row.horizon}`}>
                          <span>{row.horizon}</span>
                          <b>{row.hitRatePct == null ? "N/A" : `${row.hitRatePct.toFixed(1)}%`}</b>
                          <small>n={row.samples} · {validationStatusLabel(row.status)}</small>
                        </div>
                      ))}
                    </div>
                    <details>
                      <summary>해석 주의사항</summary>
                      <p>{rows.find((row) => row.status === "ready")?.note ?? rows.find((row) => row.status === "limited")?.note ?? rows[0]?.note}</p>
                      <p>발표 당시 빈티지와 개정 전 데이터는 재현하지 않은 관측월 기준 사후검증입니다.</p>
                    </details>
                  </article>
                );
              })}
            </div>
          </details>

          {(analysis.unknowns.length > 0 || analysis.method.guardrails.length > 0) && (
            <div className="analysis-guards">
              {analysis.unknowns.length > 0 && (
                <div>
                  <strong><CircleHelp size={14}/> 아직 모르는 것</strong>
                  <ul>{analysis.unknowns.map((unknown) => <li key={unknown}>{unknown}</li>)}</ul>
                </div>
              )}
              <details>
                <summary>{analysis.method.title}</summary>
                <p>{analysis.method.summary}</p>
                <ul>{analysis.method.guardrails.map((guard) => <li key={guard}>{guard}</li>)}</ul>
              </details>
            </div>
          )}
        </>
      )}

      <details className="situation-detail-steps">
        <summary><ChevronDown size={14}/> 단계별 근거 자세히 보기</summary>
        <div className="situation-grid">
          {items.map((item, index) => (
            <article key={item.id} className={`situation-item situation-item--${item.tone}`}>
              <div className="situation-item__number">{String(index + 1).padStart(2, "0")}</div>
              <div className="situation-item__content">
                <h3>{item.title}{item.stale && <span className="situation-stale">지연</span>}</h3>
                <strong>{item.headline}</strong>
                <p>{item.explanation}</p>
                {item.evidence && <small>{item.evidence}</small>}
              </div>
            </article>
          ))}
        </div>
      </details>

      <div className="situation-panel__guide">
        <strong>읽는 순서</strong>
        <span>시장금리 → 은행 조달 → 실제 대출금리 → 주택시장 / 기업신용 → 실물경제</span>
        <span>주택은 신규대출 flow·기존 mortgage stock·기대·실거래를 분리해서 보고, 서로 충돌하면 “엇갈림”으로 남깁니다.</span>
      </div>
    </section>
  );
}
